import { useEffect, useRef } from 'react'

/**
 * useModalA11y
 *
 * Lightweight modal accessibility helper that:
 *   1. Traps Tab/Shift+Tab inside the dialog while it's open.
 *   2. Moves focus to the first focusable element on open.
 *   3. Restores focus to the previously-focused element on close.
 *   4. Calls `onClose` when Escape is pressed.
 *
 * Usage:
 *   const ref = useModalA11y(isOpen, onClose)
 *   return <div role="dialog" aria-modal="true" ref={ref}>…</div>
 *
 * Pairs with role="dialog" + aria-modal="true" + aria-labelledby on the element
 * receiving the ref. WCAG 2.1 SC 2.1.2 (No keyboard trap), 2.4.3 (Focus order),
 * 4.1.2 (Name, Role, Value).
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

export function useModalA11y(isOpen: boolean, onClose: () => void) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isOpen) return

    // Remember what had focus before the modal opened so we can restore on close.
    previouslyFocused.current = (document.activeElement as HTMLElement) || null

    // Focus the first focusable element inside the modal on next paint so the
    // node is mounted.
    const focusFirst = () => {
      const container = containerRef.current
      if (!container) return
      const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      const first = focusables[0]
      if (first) {
        first.focus()
      } else {
        // Fall back to focusing the container itself so screen readers announce it.
        container.setAttribute('tabindex', '-1')
        container.focus()
      }
    }
    const t = window.setTimeout(focusFirst, 0)

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return

      const container = containerRef.current
      if (!container) return
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null)
      if (focusables.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKey)

    return () => {
      window.clearTimeout(t)
      document.removeEventListener('keydown', handleKey)
      // Restore focus to whatever was focused before the modal opened.
      const prev = previouslyFocused.current
      if (prev && typeof prev.focus === 'function') {
        try {
          prev.focus()
        } catch {
          /* ignore */
        }
      }
    }
  }, [isOpen, onClose])

  return containerRef
}
